import { GeometryStatus } from "../types/BaseGeometry";

/**
 * Converts an RGB array to a CSS rgb/rgba string
 * 
 * @param rgb - Array of [R, G, B] values (0-255)
 * @param alpha - Optional alpha value for transparency (0-1)
 * @returns CSS color string
 */
export function rgbToCssColor(rgb: number[] | null, alpha?: number): string {
  // Default to black if no colour provided
  const [r, g, b] = rgb && rgb.length === 3 ? rgb : [0, 0, 0];
  
  if (alpha !== undefined) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Gets the colour for a given status from the statuses array.
 * Uses the DB-stored colour value.
 * 
 * @param stateId - The ID of the status
 * @param statuses - Array of geometry statuses with their colours
 * @param geometryType - The type of geometry (affects alpha for polygons)
 * @returns CSS color string
 */
export function getColorForStatus(
  stateId: number,
  statuses: GeometryStatus[],
  geometryType: string = "default"
): string {
  const status = statuses.find(s => s.id === stateId);
  
  if (!status || !status.colour) {
    return getDefaultColorForGeometryType(geometryType);
  }
  
  // Apply alpha for polygons to make them semi-transparent
  const alpha = geometryType === "Polygon" ? 0.5 : undefined;
  return rgbToCssColor(status.colour, alpha);
}

/**
 * Gets the default fallback color for a geometry type when no status is available
 * 
 * @param geometryType - The type of geometry
 * @returns Color string
 */
export function getDefaultColorForGeometryType(geometryType: string): string {
  switch (geometryType) {
    case "Point":
      return "rgba(0, 0, 255, 0.5)";
    case "Polygon":
      return "rgba(0, 255, 0, 0.3)";
    case "LineString":
    case "MultiLineString":
      return "blue";
    default:
      return "red";
  }
}

/**
 * Interface for status color mapping used in legends
 */
export interface StatusColorMapping {
  statusId: number;
  statusName: string;
  statusDescription: string;
  color: string;
}

/**
 * Generates an array of status-color mappings for display in legends
 * Uses the DB-stored colour values.
 * 
 * @param statuses - Array of geometry statuses
 * @param geometryType - Optional geometry type to adjust color alpha
 * @returns Array of status color mappings
 */
export function getStatusColorMappings(
  statuses: GeometryStatus[],
  geometryType: string = "default"
): StatusColorMapping[] {
  return statuses.map((status) => ({
    statusId: status.id,
    statusName: status.name,
    statusDescription: status.description,
    color: rgbToCssColor(status.colour, geometryType === "Polygon" ? 0.5 : undefined),
  }));
}
